import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Footprints, Stethoscope, UserRound } from 'lucide-react';
import { ApiError, get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, Input, Select } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { printSlip } from './FrontDeskDialogs';
import { safeReturnTo } from './BookAppointmentPage';
import type { SlipData } from './token-slip';

interface DoctorOption {
  id: string;
  name: string;
}

interface Candidate {
  patientId: string;
  mrn: string;
  fullName: string;
}

function bookingToArriveOf(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  return error.details.find((detail) => detail.path === 'appointmentId')?.message ?? null;
}

function candidatesOf(error: unknown): Candidate[] {
  if (!(error instanceof ApiError) || error.status !== 409) return [];
  return error.details
    .filter((detail) => detail.path === 'patientId' && detail.message.includes('|'))
    .map((detail) => {
      const [patientId, mrn, fullName] = detail.message.split('|');
      return { patientId, mrn, fullName };
    });
}

export function WalkInPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const branchId = useWorkingBranchId();
  const returnTo = safeReturnTo(params.get('returnTo'), '/appointments');

  const [patientId, setPatientId] = useState(params.get('patientId') ?? '');
  const [registering, setRegistering] = useState(false);
  const [fresh, setFresh] = useState({ fullName: '', phone: '', sex: 'male', ageYears: '' });
  const [doctorId, setDoctorId] = useState(params.get('doctorId') ?? '');
  const [type, setType] = useState<'new' | 'follow_up'>('new');
  const [priority, setPriority] = useState<'routine' | 'urgent'>('routine');
  const [priorityReason, setPriorityReason] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [bookingToArrive, setBookingToArrive] = useState<string | null>(null);

  const doctors = useQuery({
    queryKey: ['bookable-doctors'],
    queryFn: async () => (await get<{ data: DoctorOption[] }>('/org/clinicians?kind=bookable&limit=200')).data,
  });

  const submit = useMutation({
    mutationFn: (confirmNew: boolean) =>
      post<{ slip: SlipData }>('/appointments/walk-in', {
        ...(registering
          ? {
              newPatient: {
                fullName: fresh.fullName.trim(),
                phone: fresh.phone.trim(),
                sex: fresh.sex,
                ageYears: fresh.ageYears === '' ? undefined : Number(fresh.ageYears),
              },
              confirmNew,
            }
          : { patientId }),
        doctorId,
        branchId,
        type,
        priority,
        priorityReason: priority === 'urgent' ? priorityReason || null : null,
        reason: reason.trim() || undefined,
      }),
    onSuccess: async (result) => {
      toast.success(tr('opd', 'walk_in_queued'), result.slip.tokenNo ? tr('opd', 'token_x', { tokenNo: result.slip.tokenNo }) : undefined);
      await finish(result.slip);
    },
    onError: (caught) => {
      const found = candidatesOf(caught);
      setCandidates(found);
      setBookingToArrive(bookingToArriveOf(caught));
      setError(describeError(caught));
    },
  });

  const arriveBooking = useMutation({
    mutationFn: (appointmentId: string) => post<{ slip: SlipData }>(`/appointments/${appointmentId}/arrive`, {}),
    onSuccess: async (result) => {
      toast.success(tr('opd', 'arrived'), result.slip.tokenNo ? tr('opd', 'token_x', { tokenNo: result.slip.tokenNo }) : undefined);
      await finish(result.slip);
    },
    onError: (caught) => setError(describeError(caught)),
  });

  async function finish(slip: SlipData) {
    await printSlip(slip.appointmentId, slip);
    void queryClient.invalidateQueries({ queryKey: ['appointments'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    void queryClient.invalidateQueries({ queryKey: ['queue'] });
    navigate(returnTo, { replace: true });
  }

  const ready =
    Boolean(doctorId) &&
    (registering ? fresh.fullName.trim().length >= 2 && fresh.phone.trim().length >= 7 && fresh.ageYears !== '' : Boolean(patientId)) &&
    (priority === 'routine' || Boolean(priorityReason));

  const dirty = !submit.isSuccess && (Boolean(patientId) || Boolean(doctorId) || reason !== '' || fresh.fullName !== '' || fresh.phone !== '');

  return (
    <FormScreen
      title={tr('opd', 'walk_in_patient')}
      description={tr('opd', 'queue_the_patient_now_and_print_the_token')}
      icon={Footprints}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) submit.mutate(false);
      }}
    >
      <FormSection title={tr('opd', 'patient')} icon={UserRound}>
        {registering ? (
          <>
            <Field label={tr('opd', 'full_name')} required>
              <Input value={fresh.fullName} onChange={(e) => setFresh({ ...fresh, fullName: e.target.value })} />
            </Field>
            <Field label={tr('opd', 'mobile')} required>
              <PhoneText value={fresh.phone} inputMode="tel" onChange={(e) => setFresh({ ...fresh, phone: e.target.value })} />
            </Field>
            <Field label={tr('opd', 'sex')} required>
              <ChoiceButtons
                value={fresh.sex}
                onChange={(value) => setFresh({ ...fresh, sex: value })}
                options={[
                  { value: 'male', label: tr('opd', 'male') },
                  { value: 'female', label: tr('opd', 'female') },
                  { value: 'other', label: tr('opd', 'other') },
                ]}
              />
            </Field>
            <Field label={tr('opd', 'age_years')} required>
              <Input type="number" min={0} max={130} value={fresh.ageYears} onChange={(e) => setFresh({ ...fresh, ageYears: e.target.value })} />
            </Field>
            <Button type="button" variant="link" className="justify-start px-0 sm:col-span-2" onClick={() => setRegistering(false)}>
              {tr('opd', 'pick_a_registered_patient_instead')}
            </Button>
          </>
        ) : (
          <Field label={tr('opd', 'patient')} required className="sm:col-span-2">
            <PatientPicker
              value={patientId}
              onChange={(id) => {
                setPatientId(id);
                setBookingToArrive(null);
              }}
            />
            <Button type="button" variant="link" className="px-0" onClick={() => setRegistering(true)}>
              {tr('opd', 'new_patient_register_quickly')}
            </Button>
          </Field>
        )}

        {candidates.length > 0 && (
          <Alert variant="warning" className="sm:col-span-2">
            <p className="mb-2">{tr('opd', 'these_patients_have_the_same_name_and_mobile')}</p>
            <ul className="space-y-1">
              {candidates.map((candidate) => (
                <li key={candidate.patientId} className="flex items-center justify-between gap-2">
                  <span>
                    {candidate.fullName} · {candidate.mrn}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setRegistering(false);
                      setPatientId(candidate.patientId);
                      setCandidates([]);
                      setError(null);
                    }}
                  >
                    {tr('opd', 'use_this_patient')}
                  </Button>
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </FormSection>

      <FormSection title={tr('opd', 'booking_section_visit')} icon={Stethoscope}>
        <Field label={tr('opd', 'doctor')} required>
          <Select
            value={doctorId}
            onChange={(e) => {
              setDoctorId(e.target.value);
              setBookingToArrive(null);
            }}
          >
            <option value="">{tr('opd', 'select_a_doctor')}</option>
            {doctors.data?.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('opd', 'visit_type')}>
          <ChoiceButtons
            value={type}
            onChange={setType}
            options={[
              { value: 'new', label: tr('opd', 'new') },
              { value: 'follow_up', label: tr('opd', 'follow_up') },
            ]}
          />
        </Field>
        <Field label={tr('opd', 'priority')}>
          <ChoiceButtons
            value={priority}
            onChange={setPriority}
            options={[
              { value: 'routine', label: tr('opd', 'routine') },
              { value: 'urgent', label: tr('opd', 'see_first_2') },
            ]}
          />
        </Field>
        {priority === 'urgent' && (
          <Field label={tr('opd', 'why_first')} required>
            <ChoiceButtons
              layout="chips"
              value={priorityReason}
              onChange={setPriorityReason}
              required
              options={[
                { value: 'clinical', label: tr('opd', 'clinical_need') },
                { value: 'elderly', label: tr('opd', 'elderly') },
                { value: 'disability', label: tr('opd', 'disability') },
                { value: 'pregnant', label: tr('opd', 'pregnant') },
                { value: 'child', label: tr('opd', 'young_child') },
              ]}
            />
          </Field>
        )}
        <Field label={tr('opd', 'complaint')} className="sm:col-span-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </FormSection>

      {error && candidates.length === 0 && (
        <Alert variant={bookingToArrive ? 'warning' : 'destructive'}>
          <p>{error}</p>
          {bookingToArrive && (
            <Button type="button" size="sm" className="mt-2" onClick={() => arriveBooking.mutate(bookingToArrive)} loading={arriveBooking.isPending}>
              {tr('opd', 'arrive_this_booking')}
            </Button>
          )}
        </Alert>
      )}

      <FormActions
        submitLabel={tr('opd', 'queue_and_print_token')}
        loading={submit.isPending}
        incomplete={!ready}
        secondary={
          candidates.length > 0 && registering ? (
            <Button type="button" variant="outline" onClick={() => submit.mutate(true)} loading={submit.isPending}>
              {tr('opd', 'it_is_someone_new')}
            </Button>
          ) : undefined
        }
      />
    </FormScreen>
  );
}
